import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Send, Phone, Mail, MapPin, Clock, Instagram, Facebook } from "lucide-react";
import { CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Window Tinting",
  "Paint Protection Film (Coming Soon)",
  "Vinyl Wraps (Coming Soon)",
  "Ceramic Coatings (Coming Soon)",
  "Not Sure / General Inquiry",
];

const empty = { name: "", email: "", phone: "", vehicle: "", service: "Window Tinting", message: "" };

export const Booking = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.vehicle) {
      toast.error("Please fill in your name, email and vehicle.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Request sent! We'll be in touch shortly.", { description: "Thanks for choosing AS Auto Works." });
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 text-white px-0 py-3 focus:outline-none focus:border-[#D4AF37] rounded-none placeholder:text-neutral-600 transition-colors";

  return (
    <section id="booking" data-testid="booking-section" className="relative py-24 md:py-32 bg-[#070707] border-y border-white/5">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16">
        {/* Form */}
        <div>
          <span className="text-xs tracking-[0.3em] uppercase text-gold font-bold font-mono-x">Get Started</span>
          <h2 className="font-heading font-bold uppercase tracking-tight text-4xl sm:text-5xl mt-4 text-white">
            Request A <span className="gold-gradient-text">Quote</span>
          </h2>
          <p className="text-neutral-400 mt-5 max-w-md">
            Tell us about your vehicle and what you're after. We'll get back to you with pricing
            and availability.
          </p>

          <form data-testid="booking-form" onSubmit={submit} className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="sm:col-span-1">
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">Full Name *</label>
              <input data-testid="booking-name" value={form.name} onChange={update("name")} placeholder="John Doe" className={inputCls} />
            </div>
            <div className="sm:col-span-1">
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">Email *</label>
              <input data-testid="booking-email" type="email" value={form.email} onChange={update("email")} placeholder="you@email.com" className={inputCls} />
            </div>
            <div className="sm:col-span-1">
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">Phone</label>
              <input data-testid="booking-phone" value={form.phone} onChange={update("phone")} placeholder="(555) 000-0000" className={inputCls} />
            </div>
            <div className="sm:col-span-1">
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">Vehicle Make / Model *</label>
              <input data-testid="booking-vehicle" value={form.vehicle} onChange={update("vehicle")} placeholder="2022 BMW M4" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">Service</label>
              <select data-testid="booking-service" value={form.service} onChange={update("service")} className={`${inputCls} cursor-pointer`}>
                {SERVICE_OPTIONS.map((o) => (
                  <option key={o} value={o} className="bg-[#0A0A0A] text-white">{o}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-[11px] uppercase tracking-widest text-neutral-500">Message</label>
              <textarea data-testid="booking-message" value={form.message} onChange={update("message")} rows={3} placeholder="Tell us what you need..." className={`${inputCls} resize-none`} />
            </div>
            <div className="sm:col-span-2">
              <button
                data-testid="booking-submit-button"
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center gap-3 bg-gold text-black font-bold uppercase tracking-widest text-sm px-9 py-4 hover:bg-[#C5A028] transition-colors border border-[#D4AF37] disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                {loading ? "Sending..." : "Send Request"}
              </button>
            </div>
          </form>
        </div>

        {/* Contact panel */}
        <div id="contact" data-testid="contact-section" className="lg:pl-10 lg:border-l border-white/10">
          <span className="text-xs tracking-[0.3em] uppercase text-gold font-bold font-mono-x">Visit / Contact</span>
          <h3 className="font-heading font-bold uppercase tracking-tight text-3xl mt-4 text-white">Get In Touch</h3>

          <div className="mt-8 space-y-6">
            {[
              { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/[^+\d]/g, "")}`, testid: "contact-phone" },
              { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
              { icon: MapPin, label: "Location", value: CONTACT.address, testid: "contact-address" },
            ].map((c) => (
              <div key={c.label} data-testid={c.testid} className="flex items-start gap-4">
                <div className="shrink-0 w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center">
                  <c.icon size={18} className="text-gold" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-neutral-500">{c.label}</div>
                  {c.href ? (
                    <a href={c.href} className="text-white hover:text-gold transition-colors">{c.value}</a>
                  ) : (
                    <div className="text-white">{c.value}</div>
                  )}
                </div>
              </div>
            ))}

            <div data-testid="contact-hours" className="flex items-start gap-4">
              <div className="shrink-0 w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center">
                <Clock size={18} className="text-gold" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-1">Hours</div>
                {CONTACT.hours.map((h) => (
                  <div key={h.day} className="flex justify-between gap-8 text-sm text-neutral-300">
                    <span>{h.day}</span>
                    <span className="text-white">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 flex gap-4">
            <a data-testid="social-instagram" href={CONTACT.instagram} target="_blank" rel="noreferrer" className="w-11 h-11 border border-white/15 flex items-center justify-center text-neutral-300 hover:text-gold hover:border-[#D4AF37]/50 transition-colors">
              <Instagram size={18} />
            </a>
            <a data-testid="social-facebook" href={CONTACT.facebook} target="_blank" rel="noreferrer" className="w-11 h-11 border border-white/15 flex items-center justify-center text-neutral-300 hover:text-gold hover:border-[#D4AF37]/50 transition-colors">
              <Facebook size={18} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
